/**
 * Stage D — Simplified Pause: "Why are you stopping work?"
 *
 * The technician answers ONCE. That answer is the canonical Pause intent and
 * drives everything that needs it:
 *
 *   detail      the answer itself                → ticket_pause_sessions.pause_detail
 *   intent      its category                     → ticket_pause_sessions.pause_intent
 *   pauseReason the legacy string existing       → pause_reason (hold owner, reminders,
 *               consumers already read              badges, pause analytics)
 *   attention   who must respond (server-side,   → ADVISOR: front of house
 *               same transaction as the pause)      TECH_HELP: owner / manager
 *   custody     ADVISOR only: the whole-ticket   → kick_to_foh, success-only, and only
 *               handoff reason                      when no other BOH work is live
 *
 * The server owns the same catalog (public.pause_reason_catalog) and decides
 * category and legacy reason itself; this copy exists to render the question
 * and to name the custody reason. tests/unit/stageDSimplifiedPause.test.ts
 * pins the two together.
 *
 * Deliberately NOT here: any claim of customer authorization. "Customer
 * approval needed" asks for a decision; it never records one.
 *
 * Pure: no React, no Supabase. Every surface uses this same model.
 */

export type PauseIntent = 'ADVISOR' | 'TECH_HELP' | 'PARTS' | 'BLOCKED' | 'BREAK' | 'OTHER';

export type PauseDetail =
  | 'customer_decision'
  | 'estimate_needed'
  | 'parts_pricing'
  | 'advisor_question'
  | 'technical_help'
  | 'waiting_parts'
  | 'blocked'
  | 'break'
  | 'other';

export interface PauseReasonOption {
  detail: PauseDetail;
  intent: PauseIntent;
  label: string;
  /** Legacy `pause_reason` string — what existing consumers already understand. */
  pauseReason: string;
  /** ADVISOR only: the canonical PAUSE_KICK_REASONS value for a whole-ticket handoff. */
  custodyReason: string | null;
  /** Offer a short optional note (who has to act benefits from context). */
  offersNote: boolean;
}

export const PAUSE_REASON_OPTIONS: readonly PauseReasonOption[] = [
  { detail: 'customer_decision', intent: 'ADVISOR',   label: 'Customer approval or decision needed',       pauseReason: 'Customer approval',       custodyReason: 'needs_customer_approval', offersNote: true },
  { detail: 'estimate_needed',   intent: 'ADVISOR',   label: 'Found more work — estimate needed',          pauseReason: 'Estimate needed',         custodyReason: 'needs_estimate',          offersNote: true },
  { detail: 'parts_pricing',     intent: 'ADVISOR',   label: 'Parts or pricing needed from the advisor',   pauseReason: 'Parts or pricing needed', custodyReason: 'needs_parts_or_pricing',  offersNote: true },
  { detail: 'advisor_question',  intent: 'ADVISOR',   label: 'Question for the advisor',                   pauseReason: 'Advisor question',        custodyReason: 'advisor_question',        offersNote: true },
  { detail: 'technical_help',    intent: 'TECH_HELP', label: 'Need technical help',                        pauseReason: 'Technical help needed',   custodyReason: null,                      offersNote: true },
  { detail: 'waiting_parts',     intent: 'PARTS',     label: 'Waiting on parts',                           pauseReason: 'Waiting for parts',       custodyReason: null,                      offersNote: true },
  { detail: 'blocked',           intent: 'BLOCKED',   label: 'Blocked by another job or vehicle',          pauseReason: 'Blocked by other job',    custodyReason: null,                      offersNote: false },
  { detail: 'break',             intent: 'BREAK',     label: 'Break',                                      pauseReason: 'Tech on break',           custodyReason: null,                      offersNote: false },
  { detail: 'other',             intent: 'OTHER',     label: 'Something else',                             pauseReason: 'Other',                   custodyReason: null,                      offersNote: true },
] as const;

/** How the one question is laid out: grouped by who has to act. */
export const PAUSE_REASON_GROUPS: readonly { title: string; intents: readonly PauseIntent[] }[] = [
  { title: 'Advisor or customer', intents: ['ADVISOR'] },
  { title: 'Technical help', intents: ['TECH_HELP'] },
  { title: 'Waiting', intents: ['PARTS', 'BLOCKED'] },
  { title: 'Other', intents: ['BREAK', 'OTHER'] },
];

export function pauseOption(detail: string | null | undefined): PauseReasonOption | null {
  return PAUSE_REASON_OPTIONS.find((o) => o.detail === detail) ?? null;
}

/**
 * OCT28 #7: why the work is waiting on parts. Recorded as the pause note ("Wrong part — explanation"), which the
 * server stores on the pause and sends to the front office. The pause detail stays `waiting_parts`.
 */
export const PARTS_WAIT_REASONS = [
  'Wrong part', 'Missing part', 'Delayed part', 'Damaged / broken part', 'Not yet ordered', 'Additional part needed', 'Other',
] as const;
export type PartsWaitReason = typeof PARTS_WAIT_REASONS[number];

/** The note a parts pause sends: the reason, then the explanation (500 characters at most, like every pause note). */
export function composePartsNote(reason: PartsWaitReason | null, explanation: string): string {
  if (!reason) return explanation.trim();
  const e = explanation.trim();
  return (e ? `${reason} — ${e}` : reason).slice(0, 500);
}

/** Why a parts pause cannot be sent yet (null = it can): a reason is required; "Other" needs the explanation. */
export function partsPauseProblem(reason: PartsWaitReason | null, explanation: string): string | null {
  if (!reason) return 'Choose why the work is waiting on parts.';
  if (reason === 'Other' && explanation.trim() === '') return 'Say what the parts problem is.';
  return null;
}

/** The structured intent one Pause submits. */
export interface PauseIntentFacts {
  detail: PauseDetail;
  note: string | null;
}

export function normalizePauseNote(note: string | null | undefined): string | null {
  const t = (note ?? '').trim().slice(0, 500);
  return t.length > 0 ? t : null;
}

/**
 * Should a committed Pause ask for the WHOLE ticket to go to the front of
 * house? Only an advisor/customer need does; technical help, parts, breaks
 * never do. Whether it actually happens is then decided by the server's
 * `pause_left_no_other_live_work` (decidePauseFohHandoff) — attention for the
 * advisor is raised by the server either way.
 */
export function pauseRequestsFohCustody(detail: PauseDetail): boolean {
  return pauseOption(detail)?.intent === 'ADVISOR';
}

/** What the technician is told after a committed Pause, from the server's facts. */
export function pauseAttentionMessage(
  attention: string | null,
  custody: 'kick' | 'suppressed_other_work_live' | 'none',
): string | null {
  if (attention === 'pause_technical_help') return 'Technical help requested — a manager has been notified.';
  if (attention === 'pause_advisor_needed') {
    return custody === 'suppressed_other_work_live'
      ? 'The advisor has been notified. The ticket stays with the shop because another technician still has work on it.'
      : 'The advisor has been notified.';
  }
  return null;
}
